<template>
  <div :class="['gam-list-view gam-simple-list-view', getClasses]">
    <div
      v-if="$slots.header"
      :class="[
        'header',
        {
          'is-mobile': isMobile,
          'is-padded': isHorizontallyPadded && isHuge,
          'is-lightly-padded': isHorizontallyPadded && isDesktop,
        },
      ]"
    >
      <slot name="header" />
    </div>

    <div :id="id" class="gam-list-container">
      <GamSimpleIntersect v-if="!isEmpty" v-bind="getIntersect" @infinite="loadMore?.()">
        <span v-if="scrollSpacing" class="spacer" :style="getSpacerPadding"></span>

        <div
          :class="[
            'list-content',
            {
              'is-padded': isHorizontallyPadded && isHuge,
              'is-lightly-padded': isHorizontallyPadded && isDesktop,
            },
          ]"
        >
          <slot name="list"></slot>

          <GamErrorInfo class="end-of-list" v-if="!isLoading && endInfo" v-bind="endInfo"></GamErrorInfo>
        </div>

        <span v-if="scrollSpacing" class="spacer" :style="getFooterPadding"></span>
      </GamSimpleIntersect>

      <div v-if="isEmpty" class="gam-no-data-wrapper">
        <span class="spacer" :style="getSpacerPadding"></span>
        <GamErrorInfo v-if="noDataInfo" v-bind="noDataInfo"></GamErrorInfo>
        <span class="spacer" :style="getFooterPadding"></span>
      </div>
    </div>

    <ListFooter v-if="showFooter">
      <slot name="footer" />
    </ListFooter>
  </div>
</template>
<script
  setup
  lang="ts"
  generic="T extends BaseUserDto | BaseGambitDto | BaseLocationDto | BaseClubDto | SimpleTableRow"
>
import type { BaseClubDto } from '@/core/data/club/club.interface';
import type { BaseGambitDto } from '@/core/data/gambit/gambit.interface';
import type { BaseLocationDto } from '@/core/data/location/location.interface';
import type { BaseUserDto } from '@/core/data/user/user.interface';
import { isDesktop, isHuge, isMobile } from '@/core/helpers/ui.helper';
import type { GamErrorInfoType } from '@/views/composables/models/components/GamErrorInfo';
import type { SimpleTableRow } from '@/views/composables/models/components/SimpleTable';
import { computed, type StyleValue } from 'vue';
import GamErrorInfo from '../GamErrorInfo.vue';
import GamSimpleIntersect from '../GamSimpleIntersect.vue';
import ListFooter from './ListFooter.vue';

interface GamIntersectType {
  showMore?: boolean;
  noData?: boolean;
  widthSpacing?: number | string;
  itemsGap?: number;
  scrollSpacing?: boolean;
  spacerPadding?: number | string;
  footerPadding?: number | string;
}

interface Props {
  id: string;
  isEmpty?: boolean;
  selectFirst?: boolean;
  spacerPadding?: number | string;
  footerPadding?: number | string;
  itemsGap?: number;
  scrollSpacing?: boolean;
  endInfo?: GamErrorInfoType;
  noDataInfo?: GamErrorInfoType;
  isLoading?: boolean;
  loadMore?: () => void;
  isHorizontallyPadded?: boolean;
  showFooter?: boolean;
  showMore?: boolean;
  noData?: boolean;
  widthSpacing?: number | string;
}

const props = withDefaults(defineProps<Props>(), {
  selectFirst: true,
  spacerPadding: isMobile.value ? 90 : 110,
  footerPadding: isMobile.value ? 78 : 42,
  showMore: true,
  noData: false,
  threshold: 0.5,
  rootMargin: '200px 0px 0px',
  widthSpacing: 20,
  itemsGap: 8,
  scrollSpacing: true,
  endInfo: undefined,
  noDataInfo: undefined,
  loadMore: undefined,
});

const getIntersect = computed((): GamIntersectType => {
  return {
    scrollSpacing: props.scrollSpacing,
    widthSpacing: props.widthSpacing,
    showMore: props.showMore,
    noData: props.noData,
    spacerPadding: props.spacerPadding,
    footerPadding: props.footerPadding,
    itemsGap: props.itemsGap,
  };
});

const { spacerPadding, footerPadding } = props;

const getSpacerPadding = computed((): StyleValue => {
  return {
    height:
      typeof spacerPadding === 'number'
        ? `${spacerPadding}px`
        : typeof spacerPadding === 'string'
          ? props.spacerPadding
          : 0,
  };
});

const getFooterPadding = computed((): StyleValue => {
  return {
    height:
      typeof footerPadding === 'number'
        ? `${footerPadding}px`
        : typeof footerPadding === 'string'
          ? props.footerPadding
          : 0,
  };
});

const getClasses = computed(() => {
  return {
    'no-data': props.isEmpty,
    'is-mobile': isMobile.value,
  };
});
</script>

<style scoped lang="scss">
.header {
  padding-top: calc(var(--spacing-mobile-wrapper) + var(--spacing-l) + var(--spacing-xs));
  padding-right: calc(var(--spacing-l));
  padding-left: var(--spacing-l);
  padding-bottom: var(--spacing-menu);
  position: absolute;
  top: 0;
  right: 5px;
  left: 0;
  z-index: 10;
  background: linear-gradient(var(--color-dark-700) 66%, transparent 85%);
}

.header.is-padded {
  padding-right: var(--spacing-xxl);
  padding-left: var(--spacing-xxl);
}

.header.is-lightly-padded {
  padding-right: var(--spacing-l);
  padding-left: var(--spacing-l);
}

.header.is-mobile {
  padding-top: calc(var(--spacing-l) + var(--spacing-s));
  padding-right: var(--spacing-mobile-wrapper);
  padding-left: var(--spacing-mobile-wrapper);
  padding-bottom: calc(var(--spacing-l) + var(--spacing-s) + var(--spacing-s));
}

.gam-list-view {
  width: 100%;
  max-height: 100%;
  height: auto;
  display: flex;
  flex-direction: column;
  flex: 1 0 auto;

  &.no-data {
    .spacer {
      display: none;
    }
  }

  &.is-mobile {
    height: 100%;

    .gam-list-container {
      .list-content {
        padding: 0 var(--spacing-l);
      }
    }
  }

  .gam-list-container {
    width: 100%;
    height: 100%;
    display: flex;
    // flex: 1 0 auto;
    align-items: center;
    justify-content: center;

    .spacer {
      width: 100%;
      height: 0;
      transition: height var(--transition-fast);
      display: block;
    }

    .list-content {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-md);
      padding: 0 calc(var(--spacing-l) - 0px) 0 var(--spacing-l);
      // width: calc(100% - 5px);

      &.is-padded {
        padding: 0 calc(var(--spacing-xxl) - 0px) 0 var(--spacing-xxl);
      }
    }
  }

  &:after {
    content: '';
    position: absolute;
    width: 100%;
    height: calc(var(--spacing-desktop-bottom));
    background: linear-gradient(transparent, var(--color-dark-700));
    bottom: 0;
    left: 0;
    z-index: 410;
  }

  .gam-intersect-wrapper {
    width: 100%;
    height: 100%;
    max-height: 100svh;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    flex-shrink: 0;
    align-self: stretch;

    .slot-content {
      display: flex;
      width: calc(100%);
      flex-direction: column;
    }

    &.ps {
      &.dropdown {
        .intersect-loader-wrapper {
          &.show-more {
            padding-bottom: var(--spacing-l);
          }
        }
      }
    }

    .intersect-element {
      width: 100%;
      display: flex;
      justify-content: center;

      &.show-more {
        padding-bottom: var(--spacing-xxl);
      }
    }
  }
}

.end-of-list {
  margin-top: var(--spacing-xl);
}

.gam-no-data-wrapper {
  position: absolute;
  height: 100%;
  width: 100%;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
